import * as React from 'react';
import { AlphasTagsPagination } from '../Alphas/AlphaListTags/AlphasTagsPagination';
import HOCIsMobile, { HOCIsMobileState } from '../HOCIsMobile/HOCIsMobile';
import PaginationPageSizeControl from '../Pagination/PaginationPageSizeControl';

import './TablePagination.scss';

interface TablePaginationStateProps {
  data: any[];
  pageSize: number;
  page: number;
  pages: any;
  onPageChange: (n: number) => void;
  onPageSizeChange: (n: any) => void;
}

interface PaginationComponentProps {
  pageSize: number;
  page: number;
  pages: any;
  changePageSize: (e: number) => void;
  changePrevPage: () => void;
  changeNextPage: () => void;
  getPaginationNumbers: () => any[];
  changePage: (n: number) => void;
  hasButtons: boolean;
  numOfResults: number;
  removeAlphas: () => void;
  compareAlphas: () => void;
  pageSizeLimit: number;
  additionalMessage: string | undefined;
  isSearchLimitWarningShown?: boolean;
}

const Pagination = AlphasTagsPagination(PaginationPageSizeControl);

const PaginationComponent = ({
  changePageSize,
  pageSize,
  numOfResults,
  pageSizeLimit,
  getPaginationNumbers,
  page,
  pages,
  changePage,
  changePrevPage,
  changeNextPage,
  additionalMessage,
  isSearchLimitWarningShown,
}: PaginationComponentProps): JSX.Element => {
  return (
    <Pagination
      changePageSize={changePageSize}
      pageSize={pageSize}
      itemsCount={numOfResults}
      pageSizeLimit={pageSizeLimit}
      getPaginationNumbers={getPaginationNumbers}
      page={page}
      pages={pages}
      changePage={changePage}
      changePrevPage={changePrevPage}
      changeNextPage={changeNextPage}
      allowPageSizeChange={true}
      additionalMessage={additionalMessage}
      isSearchLimitWarningShown={isSearchLimitWarningShown}
    />
  );
};

const emptyFunction = () => {
  return;
};

export interface TablePaginationProps {
  hasButtons?: boolean;
  numOfResults?: number;
  compareAlphas?: () => void;
  removeAlphas?: () => void;
  pageSizeLimit?: number;
  additionalMessage?: string | undefined;
  isSearchLimitWarningShown?: boolean;
  pageSize?: number;
  page?: number;
  pages?: number;
  onPageChange?: (n: number) => void;
  onPageSizeChange?: (n: any) => void;
}

const TablePagination = ({
  hasButtons = false,
  numOfResults = 0,
  compareAlphas = emptyFunction,
  removeAlphas = emptyFunction,
  pageSizeLimit = 30,
  pageSize,
  page,
  pages,
  additionalMessage,
  isSearchLimitWarningShown,
  onPageSizeChange,
  onPageChange,
}: TablePaginationProps) => {
  const TablePaginationInner = (tableProps: TablePaginationStateProps & HOCIsMobileState) => {
    const currentPage = tableProps.page || page || 0;
    const currentPageSize = tableProps.pageSize || pageSize || 20;
    const handlePageChange = tableProps.onPageChange || onPageChange;
    const handleChangePageSize = tableProps.onPageSizeChange || onPageSizeChange;
    const totalPages = tableProps.pages || pages;

    const getSafePage = (candidatePage: any) => {
      if (isNaN(candidatePage)) {
        candidatePage = tableProps.page;
      }

      return Math.min(Math.max(candidatePage, 0), totalPages - 1);
    };

    const changePage = (page: any) => {
      page = getSafePage(page);

      if (currentPage !== page) {
        handlePageChange(page);
      }
    };

    const changePrevPage = () => {
      changePage(currentPage - 1);
    };

    const changeNextPage = () => {
      changePage(currentPage + 1);
    };

    const getPaginationNumbers = () => {
      const current = currentPage;
      const last = totalPages;
      const delta = tableProps.isMobile ? 0 : 2;
      const left = current - delta;
      const right = current + delta + 1;
      const range = [];
      const rangeWithDots = [];
      let l = null;

      for (let i = 1; i <= last; i = i + 1) {
        if (delta === 0) {
          if (i === 1 || i === last || i === right) {
            range.push(i);
          }
        } else {
          if (i === 1 || i === last || (i >= left && i < right)) {
            range.push(i);
          }
        }
      }

      for (const i of range) {
        if (l) {
          if (i - l === 2) {
            rangeWithDots.push(l + 1);
          } else if (i - l !== 1) {
            rangeWithDots.push('...');
          }
        }
        rangeWithDots.push(i);
        l = i;
      }

      return rangeWithDots;
    };

    return (
      <PaginationComponent
        changePageSize={handleChangePageSize}
        pageSize={currentPageSize}
        page={currentPage}
        pages={totalPages}
        changePrevPage={changePrevPage}
        changeNextPage={changeNextPage}
        getPaginationNumbers={getPaginationNumbers}
        changePage={changePage}
        hasButtons={hasButtons}
        compareAlphas={compareAlphas}
        numOfResults={numOfResults}
        removeAlphas={removeAlphas}
        pageSizeLimit={pageSizeLimit}
        additionalMessage={additionalMessage}
        isSearchLimitWarningShown={isSearchLimitWarningShown}
      />
    );
  };

  return HOCIsMobile(TablePaginationInner);
};

export default TablePagination;
